<div class="conn-card store-card">
  <section class="store-shell">
    <div class="store-header">
      <div>
        <h1>Your Online Store <span class="store-status-badge" [ngClass]="storeStatusClass" *ngIf="!isYouksStoreUser">{{ storeStatusLabel }}</span></h1>
        <p>Easily update your online store with our user-friendly editor.</p>
      </div>
    </div>

    <div class="store-form business-info-locked" *ngIf="!isBusinessInfoComplete">
      <div class="business-info-lock-message">
        <i class="pi pi-lock"></i>
        <div>
          <strong>Business information required</strong>
          <p>Please complete your business information before setting up your online store.</p>
        </div>
        <button
          pButton
          type="button"
          label="Complete business information"
          icon="pi pi-arrow-right"
          (click)="goToBusinessInfo()"
        ></button>
      </div>
    </div>

    <div class="store-form" [class.store-created]="showStoreStatus" *ngIf="isBusinessInfoComplete">
      <label class="field-label">
        <span>Free LincSell subdomain <span class="required">*</span></span>
        <div class="subdomain-row">
          <input
            pInputText
            type="text"
            [ngModel]="subdomain"
            (input)="onSubdomainInput($event)"
            (blur)="onSubdomainBlur()"
            placeholder="timelore"
            [readonly]="showStoreStatus || isBusy"
          />
          <span *ngIf="!isAiEditorStore">.lincsell.com</span>
        </div>
        <ng-container *ngIf="!showStoreStatus">
          <small class="field-hint error" *ngIf="subdomainTooShort">At least {{ minSubdomainLength }} characters.</small>
          <small class="field-hint checking" *ngIf="!subdomainTooShort && isCheckingDomainAvailability">Checking availability...</small>
          <small class="field-hint error" *ngIf="!subdomainTooShort && !isCheckingDomainAvailability && domainAvailable === false">{{ domainUnavailableReason || 'This subdomain is already taken. Try another.' }}</small>
          <small class="field-hint success" *ngIf="!subdomainTooShort && !isCheckingDomainAvailability && domainAvailable === true">Available.</small>
        </ng-container>
      </label>

      <label class="field-label brand-colors-field" *ngIf="!showStoreStatus">
        <span>Brand colors <span class="required">*</span></span>
        <p-multiSelect
          [options]="brandColorOptions"
          [(ngModel)]="selectedBrandColors"
          [disabled]="isBusy"
          optionLabel="name"
          optionValue="code"
          defaultLabel="Select brand colors"
          display="chip"
          [filter]="true"
          filterBy="name"
          appendTo="body"
          styleClass="brand-color-multiselect"
          panelStyleClass="brand-color-multiselect-panel"
        >
          <ng-template let-colors pTemplate="selectedItems">
            <ng-container *ngIf="colors?.length; else brandColorPlaceholder">
              <span
                class="brand-color-dropdown-chip"
                *ngFor="let colorCode of colors"
              >
                <span class="brand-color-dot" [style.background-color]="getBrandColorOption(colorCode)?.hex"></span>
                <span>{{ getBrandColorOption(colorCode)?.name }}</span>
                <button
                  type="button"
                  class="brand-color-chip-remove"
                  [attr.aria-label]="'Remove ' + getBrandColorOption(colorCode)?.name"
                  (click)="removeSelectedBrandColor(colorCode, $event)"
                ><i class="pi pi-times"></i></button>
              </span>
            </ng-container>
            <ng-template #brandColorPlaceholder>Select brand colors</ng-template>
          </ng-template>
          <ng-template let-color pTemplate="item">
            <span class="brand-color-option">
              <span class="brand-color-dot" [style.background-color]="color.hex"></span>
              <span class="brand-color-option-name">{{ color.name }}</span>
              <i class="pi pi-check brand-color-option-check" *ngIf="selectedBrandColors.includes(color.code)"></i>
            </span>
          </ng-template>
        </p-multiSelect>
      </label>

      <div class="field-label brand-colors-field brand-colors-readonly" *ngIf="showStoreStatus && selectedBrandColors.length">
        <span>Brand colors <span class="required">*</span></span>
        <div class="brand-color-readonly-list">
          <span
            class="brand-color-selected-chip"
            *ngFor="let colorCode of selectedBrandColors"
            [style.background-color]="getBrandColorOption(colorCode)?.hex"
            [style.color]="getBrandColorTextColor(colorCode)"
          >{{ getBrandColorOption(colorCode)?.name }}</span>
        </div>
      </div>

      <ng-container *ngIf="!selectedTemplate && !showStoreStatus; else selectedTemplateState">
        <button
          pButton
          type="button"
          icon="pi pi-th-large"
          label="Choose template"
          class="choose-template-button"
          (click)="openTemplateDialog()"
        ></button>
        <p class="selection-hint">Choose a template to continue.</p>
      </ng-container>

      <ng-template #selectedTemplateState>
        <section class="selected-template-card selected-template-loading" aria-label="Loading selected store template" *ngIf="!selectedTemplate && showStoreStatus && isLoadingAiEditorTemplates">
          <div class="selected-template-preview selected-template-preview-skeleton"></div>
          <div class="selected-template-footer">
            <div>
              <span>Template selected</span>
              <strong><i class="pi pi-spin pi-spinner"></i> Loading template&hellip;</strong>
            </div>
          </div>
        </section>

        <section class="selected-template-card" aria-label="Selected store template" *ngIf="selectedTemplate">
          <div
            class="selected-template-preview"
            role="img"
            [attr.aria-label]="selectedTemplate.name + ' store template preview'"
            [style.background-image]="'url(' + selectedTemplate.image + ')'"
          ></div>
          <div class="selected-template-footer">
            <div>
              <span>Template selected</span>
              <strong>{{ selectedTemplate.name }}</strong>
            </div>
            <button
              *ngIf="!showStoreStatus"
              pButton
              type="button"
              icon="pi pi-th-large"
              label="Change template"
              class="p-button-outlined change-template-button"
              [disabled]="isBusy"
              (click)="openTemplateDialog()"
            ></button>
          </div>
        </section>

        <p class="template-warning" *ngIf="!showStoreStatus">Choose carefully — your store template cannot be changed once your store is created.</p>

        <div class="terms-consent-field" *ngIf="!showStoreStatus">
          <p-checkbox
            [binary]="true"
            [(ngModel)]="termsAccepted"
            [disabled]="isBusy"
            inputId="onlineStoreTerms"
          ></p-checkbox>
          <label for="onlineStoreTerms">
            I agree to the <a href="https://lincsell.com/terms-of-service/" target="_blank" rel="noopener">Terms &amp; Conditions</a> and <a href="https://lincsell.com/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a>, including prohibited use, restricted products/services, and policy violations.
          </label>
        </div>

        <div class="creation-actions" *ngIf="!showStoreStatus">
             <button
            pButton
            type="button"
            icon="pi pi-check"
            label="Create store"
            [loading]="isSavingStore"
            [disabled]="!canCreateStore"
            (click)="createStore()"
          ></button>
          <button
            pButton
            type="button"
            class="custom-store-button"
            label="Contact us for a custom online store"
            [loading]="isSubmittingCustomStoreRequest"
            [disabled]="!canRequestCustomStore"
            (click)="requestCustomOnlineStore()"
          ></button>
       
        </div>
        <p class="creation-hint" *ngIf="!showStoreStatus">{{ isAiEditorStore ? 'Create your store to publish it.' : 'Create your store to publish it, or contact us for a fully custom build.' }}</p>

        <ng-container *ngIf="showStoreStatus">
          <div class="completed-store-actions">
            <button
              pButton
              type="button"
              icon="pi pi-eye"
              label="Preview online store"
              class="p-button-outlined preview-store-button"
              [disabled]="!canPreviewStore"
              (click)="previewOnlineStore()"
            ></button>
            <button
              pButton
              type="button"
              icon="pi pi-pencil"
              label="Edit online store"
              class="edit-store-button"
              *ngIf="!isYouksStoreUser"
              [disabled]="!canPreviewStore || isOpeningStoreEditor || isMobileDevice"
              [loading]="isOpeningStoreEditor"
              (click)="goToStoreEditor()"
            ></button>
          </div>
          <p class="completed-store-message" *ngIf="isCustomStorePending">
            Our team is building your custom online store. It will be ready within the next 3 business days, and we&rsquo;ll contact you at your business email address once it&rsquo;s ready. The &ldquo;Preview Online Store&rdquo; and &ldquo;Edit Online Store&rdquo; buttons will also be enabled at that time.
          </p>
          <p class="completed-store-message" *ngIf="isMobileDevice && !isCustomStorePending && !isYouksStoreUser">
            The store editor isn&rsquo;t available on mobile. Please use a desktop or laptop to edit your online store.
          </p>
          <p class="completed-store-message" *ngIf="!isCustomStorePending && !isYouksStoreUser">
            Your store template and domain are locked in and can no longer be changed here. Contact us if you need a custom build.
          </p>
          <p class="completed-store-message" *ngIf="!isCustomStorePending && isYouksStoreUser">
            Preview your online store to see exactly what your customers see. Update your store anytime to keep its look and content fresh and up to date.
          </p>
        </ng-container>
      </ng-template>
    </div>
  </section>
</div>

<p-dialog
  [(visible)]="isTemplateDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '900px' }"
  [breakpoints]="{ '960px': 'calc(100vw - 32px)' }"
  styleClass="online-store-template-dialog"
>
  <ng-template pTemplate="header">
    <div class="dialog-heading">
      <h2>Choose your store template</h2>
      <p>Pick the template for your online store.</p>
    </div>
  </ng-template>

  <div class="template-dialog-body">
    <span class="dialog-field-label">Store template <span class="required">*</span></span>
    <div class="template-grid" role="radiogroup" aria-label="Store template" *ngIf="!isLoadingAiEditorTemplates; else templateGridSkeleton">
      <button
        type="button"
        class="template-card"
        *ngFor="let template of storeTemplates"
        [class.selected]="pendingTemplateKey === template.key"
        [attr.aria-checked]="pendingTemplateKey === template.key"
        role="radio"
        (click)="selectTemplate(template.key)"
      >
        <span
          class="template-preview"
          role="img"
          [attr.aria-label]="template.name + ' store template preview'"
          [style.background-image]="'url(' + template.image + ')'"
        ></span>
        <span class="template-copy">
          <span class="template-name-row">
            <strong>{{ template.name }}</strong>
            <span class="template-radio" [class.checked]="pendingTemplateKey === template.key"></span>
          </span>
          <small>{{ template.description }}</small>
          <button *ngIf="template.demoUrl" type="button" class="demo-link" (click)="previewTemplate(template, $event)">View live demo <i class="pi pi-arrow-up-right"></i></button>
        </span>
      </button>
    </div>
    <ng-template #templateGridSkeleton>
      <div class="template-grid" aria-hidden="true">
        <div class="template-card template-card-skeleton" *ngFor="let i of [1, 2]">
          <span class="template-preview template-preview-skeleton"></span>
          <span class="template-copy">
            <span class="template-name-row">
              <strong class="skeleton-line skeleton-line-title"></strong>
            </span>
            <small class="skeleton-line skeleton-line-text"></small>
          </span>
        </div>
      </div>
    </ng-template>
  </div>

  <ng-template pTemplate="footer">
    <div class="dialog-footer">
      <span>{{ pendingTemplateKey ? 'Save this template to continue.' : 'Select a template to continue.' }}</span>
      <button
        pButton
        type="button"
        label="Save"
        [disabled]="!pendingTemplateKey"
        (click)="confirmTemplate()"
      ></button>
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [(visible)]="customStoreRequestVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="false"
  [closeOnEscape]="false"
  [dismissableMask]="false"
  [showHeader]="false"
  [style]="{ width: '360px' }"
  styleClass="custom-store-request-dialog"
>
  <div class="custom-store-request-content">
    <span class="custom-store-request-icon" aria-hidden="true">
      <i class="pi pi-clock"></i>
    </span>
    <div class="custom-store-request-copy">
      <h2>We have received your custom online store request</h2>
      <p>
        Our team is working on your online store and it will be ready in the next 3 business days.
        We will contact you<span *ngIf="customStoreContactEmail"> on email <strong>({{ customStoreContactEmail }})</strong></span>
        when it&rsquo;s ready.
      </p>
      <button
        pButton
        type="button"
        label="Got it"
        class="custom-store-request-dismiss"
        (click)="closeCustomStoreRequestConfirmation()"
      ></button>
    </div>
  </div>
</p-dialog>

<p-dialog
  [(visible)]="isTermsDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '640px' }"
  [breakpoints]="{ '960px': 'calc(100vw - 32px)' }"
  styleClass="online-store-terms-dialog"
  header="Online Store Terms &amp; Conditions"
>
  <div class="terms-dialog-body">
    <h3>1. Prohibited Use</h3>
    <p>
      Your online store must not be used to sell, promote, or distribute illegal goods or services,
      engage in fraudulent activity, infringe on intellectual property rights, distribute malware, or
      otherwise violate applicable laws and regulations.
    </p>
    <h3>2. Restricted Products/Services</h3>
    <p>
      Certain product and service categories are restricted or require additional approval, including
      (without limitation) weapons and ammunition, controlled substances, counterfeit goods, adult
      content, tobacco and vaping products, gambling services, and financial or investment products.
      Contact support before listing items in a restricted category.
    </p>
    <h3>3. Policy Violations</h3>
    <p>
      Stores found in violation of these terms may be suspended or terminated without notice.
      Repeated or severe violations may also result in the loss of access to LincSell services.
      You are responsible for ensuring your store and its listings remain compliant at all times.
    </p>
  </div>
  <ng-template pTemplate="footer">
    <button
      pButton
      type="button"
      label="Close"
      (click)="closeTermsDialog()"
    ></button>
  </ng-template>
</p-dialog>
